import { useEffect, useRef, useState } from "react";
import { Search, X, Plus } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Symbol search modal — calls /api/symbols/search (TradingView proxy).
 * onSelect(item) receives { symbol, tv_symbol, name, category }.
 */
export default function SymbolSearch({ open, onClose, onSelect, onAddToWatchlist }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const tRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    if (!open) {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(tRef.current);
    if (!q || q.trim().length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/symbols/search`, { params: { q } });
        setResults(data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(tRef.current);
  }, [q, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={onClose} data-testid="symbol-search-modal">
      <div className="w-full max-w-2xl bg-[#121212] border border-zinc-800 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search any symbol — AAPL, BTC, NIFTY, EURUSD, Tesla…"
            className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-zinc-600"
            data-testid="symbol-search-input"
          />
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="symbol-search-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-auto">
          {loading && <div className="px-4 py-6 text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="symbol-search-loading">◢ Searching…</div>}
          {!loading && q.length >= 1 && results.length === 0 && (
            <div className="px-4 py-6 text-xs font-mono uppercase tracking-widest text-zinc-500" data-testid="symbol-search-empty">No matches. Try a different query.</div>
          )}
          {!loading && q.length === 0 && (
            <div className="px-4 py-6 text-xs text-zinc-500">
              <div className="font-mono uppercase tracking-widest mb-2 text-zinc-400">Examples</div>
              <div className="flex flex-wrap gap-2">
                {["NIFTY", "BANKNIFTY", "BTCUSD", "AAPL", "TSLA", "EURUSD", "XAUUSD", "INFY", "HDFCBANK"].map((s) => (
                  <button key={s} onClick={() => setQ(s)} className="px-2 py-1 border border-zinc-800 hover:border-zinc-600 font-mono text-xs">{s}</button>
                ))}
              </div>
            </div>
          )}
          {results.map((r, idx) => (
            <div
              key={`${r.tv_symbol}-${idx}`}
              className="px-4 py-3 border-b border-zinc-900 hover:bg-[#1a1a1a] flex items-center justify-between gap-3 cursor-pointer"
              onClick={() => { onSelect(r); onClose(); }}
              data-testid={`symbol-result-${idx}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-mono text-sm">
                  <span className="font-bold text-white">{r.tv_symbol}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#007AFF] border border-[#007AFF]/30 px-1.5">{r.category}</span>
                </div>
                <div className="text-xs text-zinc-400 mt-0.5 truncate">{r.name}</div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onAddToWatchlist(r); }}
                className="text-zinc-500 hover:text-[#007AFF] text-xs uppercase tracking-widest font-bold flex items-center gap-1 shrink-0 px-2 py-1 border border-zinc-800 hover:border-[#007AFF]"
                data-testid={`symbol-add-${idx}`}
              >
                <Plus className="h-3 w-3" /> Watchlist
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-zinc-800 px-4 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-600 font-mono">
          <span>Powered by TradingView symbol search</span>
          <span>↵ select · esc close</span>
        </div>
      </div>
    </div>
  );
}
